<template>
  <a-card title="URLs under this domain" size="small" class="domain-urls-card">
    <p class="panel-help mb-2">
      This list is read-only and shows monitored URLs that match the selected host/domain.
    </p>

    <a-spin :spinning="loading">
      <template v-if="loadError">
        <a-alert type="error" show-icon message="Could not load URLs for this domain." />
        <div class="mt-2">
          <a-button type="primary" size="small" @click="$emit('retry')">
            Retry
          </a-button>
        </div>
      </template>

      <template v-else-if="normalizedRows.length === 0">
        <a-alert type="info" show-icon message="No URLs found for this host/domain." />
      </template>

      <template v-else>
        <a-table
          :columns="columns"
          :data-source="normalizedRows"
          :pagination="false"
          :scroll="{ y: 420 }"
          size="small"
          row-key="id"
          :rowClassName="rowClassName"
        >
          <template slot="serial_number" slot-scope="text, record, index">
            {{ index + 1 }}
          </template>
          <template slot="url_name" slot-scope="text">
            <span :class="{ 'url-link-highlighted': isHighlightedUrl(text) }">
              {{ text }}
            </span>
          </template>
          <template slot="health_status" slot-scope="text">
            {{ text || '-' }}
          </template>
        </a-table>
      </template>
    </a-spin>
  </a-card>
</template>

<script>
import {
  normalizeComparableUrl as normalizeComparableUrlUtil,
} from '../utils/domain.utils';

export default {
  name: 'AiTriageDomainUrlsPanel',
  props: {
    rows: {
      type: Array,
      default: () => [],
    },
    loading: {
      type: Boolean,
      default: false,
    },
    loadError: {
      type: Boolean,
      default: false,
    },
    highlightedUrl: {
      type: String,
      default: '',
    },
  },
  computed: {
    normalizedRows() {
      return (Array.isArray(this.rows) ? this.rows : []).map((r) => ({
        id: r.id,
        url_name: r.url_name || '',
        health_status: r.health_status || '',
      }));
    },
    columns() {
      return [
        {
          title: 'No.',
          dataIndex: 'serial_number',
          key: 'serial_number',
          width: 70,
          scopedSlots: { customRender: 'serial_number' },
        },
        {
          title: 'URL',
          dataIndex: 'url_name',
          key: 'url_name',
          scopedSlots: { customRender: 'url_name' },
          ellipsis: true,
        },
        {
          title: 'Status',
          dataIndex: 'health_status',
          key: 'health_status',
          width: 120,
          scopedSlots: { customRender: 'health_status' },
        },
      ];
    },
  },
  methods: {
    normalizeComparableUrl(rawUrl) {
      return normalizeComparableUrlUtil(rawUrl);
    },
    isHighlightedUrl(url) {
      const needle = this.normalizeComparableUrl(this.highlightedUrl);
      if (!needle) {
        return false;
      }
      return this.normalizeComparableUrl(url) === needle;
    },
    rowClassName(record) {
      return this.isHighlightedUrl(record && record.url_name) ? 'url-row-highlighted' : '';
    },
  },
};
</script>

<style lang="scss" scoped>
.domain-urls-card ::v-deep .ant-card-body {
  display: flex;
  flex-direction: column;
}

.panel-help {
  font-size: 12px;
  color: #8c8c8c;
}

.domain-urls-card ::v-deep .url-row-highlighted > td {
  background: #fffbe6;
}

.domain-urls-card ::v-deep .url-link-highlighted {
  color: #d46b08;
  font-weight: 600;
}
</style>
